(() => {
  const THEME_ID = '200652751183';
  const ROOT_PATH = window.Shopify?.routes?.root || '/';
  const money = (pence) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format((Number(pence) || 0) / 100);
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]);
  const safeId = (value) => String(value || '').replace(/[^a-zA-Z0-9_-]/g, '-');
  const activityTypes = new Set(['run', 'cycle', 'triathlon', 'other']);

  function registryFor(root) {
    const script = root.parentElement?.querySelector('[data-rsg-fuel-packs-registry]') || document.querySelector('[data-rsg-fuel-packs-registry]');
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch (error) { return null; }
  }

  function inventoryFor(root) {
    const script = root.parentElement?.querySelector('[data-rsg-fuel-packs-inventory]') || document.querySelector('[data-rsg-fuel-packs-inventory]');
    if (!script) return null;
    try {
      const inventory = JSON.parse(script.textContent);
      if (inventory?.schemaVersion !== 1 || inventory?.targetThemeId !== THEME_ID || !inventory?.variants || typeof inventory.variants !== 'object') return null;
      return inventory.variants;
    } catch (error) {
      return null;
    }
  }

  function analyticsAllowed() {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true; } catch (error) { return false; }
  }

  function track(name, payload) {
    if (!analyticsAllowed() || typeof window.Shopify?.analytics?.publish !== 'function') return;
    const safePayload = {
      source: String(payload.source || 'fuel_packs').slice(0, 40),
      event_id: String(payload.eventId || '').slice(0, 40),
      brand: String(payload.brand || '').slice(0, 80),
      pack_id: String(payload.packId || '').slice(0, 100),
      component_count: Number(payload.componentCount || 0),
      value_pence: Number(payload.valuePence || 0),
      currency: 'GBP'
    };
    window.Shopify.analytics.publish(name, safePayload);
  }

  function isCandidatePreview() {
    return Number(window.Shopify?.theme?.id) === Number(THEME_ID)
      && window.Shopify?.theme?.role === 'unpublished'
      && window.Shopify?.previewMode === true;
  }

  function routeWithParams(path, values = {}) {
    const url = new URL(path, window.location.origin);
    for (const [key, value] of Object.entries(values)) {
      if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
    }
    url.searchParams.delete('preview_theme_id');
    if (isCandidatePreview()) url.searchParams.set('preview_theme_id', THEME_ID);
    return `${url.pathname}${url.search}${url.hash}`;
  }

  function contextFor(root) {
    const params = new URLSearchParams(window.location.search);
    const requestedActivity = root?.dataset.rsgFuelPacksActivity || params.get('activity') || 'other';
    const requestedGuideRoute = String(root?.dataset.rsgFuelPacksGuideRoute || params.get('guide') || '').slice(0, 160);
    return {
      activity: activityTypes.has(requestedActivity) ? requestedActivity : 'other',
      label: String(root?.dataset.rsgFuelPacksContext || params.get('context') || 'event or long session').slice(0, 60),
      guideRoute: /^\/pages\/[a-z0-9-]+$/i.test(requestedGuideRoute) ? requestedGuideRoute : ''
    };
  }

  function isFuelPacksRoute() {
    if (window.location.pathname === '/pages/fuel-packs') return true;

    const isPreviewDonor = window.location.pathname === '/pages/us-ready-sweat-go'
      && new URLSearchParams(window.location.search).get('view') === 'fuel-packs';

    return isCandidatePreview() && isPreviewDonor;
  }

  function finderUrl(pack, source = 'fuel_pack', context = {}) {
    return routeWithParams('/pages/rsg-fuel-finder', {
      rsg_pack_source: source,
      rsg_event: pack.eventId,
      rsg_pack_label: pack.eventLabel,
      rsg_activity_type: context.activity || pack.fuelFinderPrefill.activity,
      rsg_distance_km: pack.fuelFinderPrefill.distanceKm,
      rsg_duration_hours: pack.durationHours,
      rsg_brand: pack.brand
    });
  }

  function eventUrl(eventId, context = {}) {
    return routeWithParams('/pages/fuel-packs', { distance: eventId, activity: context.activity, context: context.label, guide: context.guideRoute });
  }

  function detailUrl(pack, context = {}) {
    return routeWithParams('/pages/fuel-packs', { pack: pack.id, activity: context.activity, context: context.label, guide: context.guideRoute });
  }

  function liveVariant(product, variantId) {
    return product?.variants?.find((variant) => String(variant.id) === String(variantId));
  }

  function provenInventoryQuantity(value) {
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  }

  async function refreshRegistry(registry, inventoryVariants) {
    const handles = [...new Set(registry.packs.flatMap((pack) => pack.components.map((component) => component.handle)))];
    const products = new Map();
    await Promise.all(handles.map(async (handle) => {
      try {
        const response = await fetch(`${ROOT_PATH}products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Product check returned ${response.status}`);
        products.set(handle, await response.json());
      } catch (error) {
        products.set(handle, null);
      }
    }));
    registry.packs.forEach((pack) => {
      let verified = true;
      let available = true;
      let currentTotal = 0;
      pack.components.forEach((component) => {
        const product = products.get(component.handle);
        const variant = liveVariant(product, component.variantId);
        if (!product || !variant) {
          verified = false;
          available = false;
          return;
        }
        const price = Number(variant.price);
        const inventoryRecord = inventoryVariants?.[component.variantId];
        const inventoryQuantity = inventoryRecord?.handle === component.handle
          ? provenInventoryQuantity(inventoryRecord.inventoryQuantity)
          : null;
        component.livePricePence = Number.isFinite(price) ? price : component.pricePence;
        component.liveAvailable = variant.available === true;
        component.liveInventoryQuantity = inventoryQuantity;
        currentTotal += component.livePricePence * component.quantity;
        if (component.liveInventoryQuantity === null) verified = false;
        if (!component.liveAvailable || component.liveInventoryQuantity === null || component.liveInventoryQuantity < component.quantity) available = false;
      });
      pack.liveVerified = verified;
      pack.liveAvailable = verified && available;
      pack.livePricePence = verified ? currentTotal : pack.pricePence;
    });
  }

  function statusLabel(pack) {
    if (!pack.liveVerified) return '<span class="rsg-fuel-pack-card__status rsg-fuel-pack-card__status--check">Check again soon</span>';
    if (!pack.liveAvailable) return '<span class="rsg-fuel-pack-card__status rsg-fuel-pack-card__status--out">Currently unavailable</span>';
    return '<span class="rsg-fuel-pack-card__status rsg-fuel-pack-card__status--in">Available now</span>';
  }

  function nutritionSummary(pack, context = 'event') {
    const totals = context === 'pack' ? pack.packNutrition : pack.eventNutrition;
    const facts = [`${Math.round(totals.carbohydrateG)} g carbohydrate`];
    if (totals.sodiumMg > 0) facts.push(`${Math.round(totals.sodiumMg)} mg sodium`);
    if (totals.caffeineMg > 0) facts.push(`${Math.round(totals.caffeineMg)} mg caffeine`);
    return facts.map((fact) => `<li>${escapeHtml(fact)}</li>`).join('');
  }

  function transformedPdpImage(source, width = 720) {
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return source;
    }
  }

  function packPicture(pack, context = 'card', routeContext = {}) {
    if (!pack.image?.src) return '';
    const detail = context === 'detail';
    const compact = context === 'compact';
    const sources = Array.isArray(pack.image.sources) ? pack.image.sources : [];
    const srcset = sources.filter(source => source.src && Number(source.width) > 0).map(source => `${escapeHtml(source.src)} ${Number(source.width)}w`).join(', ');
    const image = `<img src="${escapeHtml(pack.image.src)}"${srcset ? ` srcset="${srcset}"` : ''} width="${escapeHtml(pack.image.width)}" height="${escapeHtml(pack.image.height)}" alt="${escapeHtml(pack.image.alt)}" title="${escapeHtml(pack.image.title)}" loading="${detail ? 'eager' : 'lazy'}" decoding="async"${detail ? ' fetchpriority="high"' : ''} sizes="${compact ? '(max-width: 700px) 42vw, 14vw' : detail ? '(max-width: 700px) 92vw, 34vw' : '(max-width: 700px) 92vw, (max-width: 1050px) 45vw, 30vw'}">`;
    return `<figure class="rsg-fuel-pack-image rsg-fuel-pack-image--${escapeHtml(context)}">${detail ? image : `<a href="${escapeHtml(detailUrl(pack, routeContext))}" aria-label="View ${escapeHtml(pack.title)}">${image}</a>`}<figcaption class="visually-hidden">${escapeHtml(pack.image.description)}</figcaption></figure>`;
  }

  function supportingGallery(pack) {
    const items = pack.image?.supporting || [];
    if (!items.length) return '';
    return `<section class="rsg-fuel-pack-detail__supporting" aria-labelledby="RsgFuelPackSupporting-${safeId(pack.id)}"><h2 id="RsgFuelPackSupporting-${safeId(pack.id)}">Pack contents up close</h2><p>Take a closer look at each product included.</p><div>${items.map((item) => `<a href="${escapeHtml(routeWithParams(item.pdpRoute))}"><img src="${escapeHtml(transformedPdpImage(item.src))}" width="720" height="720" alt="${escapeHtml(item.alt)}" loading="lazy" decoding="async"><span>View ${escapeHtml(item.productTitle)}</span></a>`).join('')}</div></section>`;
  }

  function packCard(pack, compact = false, routeContext = {}) {
    const price = pack.livePricePence ?? pack.pricePence;
    const contents = pack.components.map((component) => `${component.quantity} × ${component.saleUnitLabel}`).join(', ');
    return `<article class="rsg-fuel-pack-card${compact ? ' rsg-fuel-pack-card--compact' : ''}" data-rsg-pack-card="${escapeHtml(pack.id)}">
      ${packPicture(pack, compact ? 'compact' : 'card', routeContext)}
      <div class="rsg-fuel-pack-card__top"><p class="rsg-fuel-pack-card__brand">${escapeHtml(pack.brand)}</p>${statusLabel(pack)}</div>
      <h3><a href="${escapeHtml(detailUrl(pack, routeContext))}" data-rsg-pack-select="${escapeHtml(pack.id)}">${escapeHtml(pack.title)}</a></h3>
      <p class="rsg-fuel-pack-card__difference">${escapeHtml(pack.differentiator)}</p>
      <dl class="rsg-fuel-pack-card__facts">
        <div><dt>Plan length</dt><dd>Around ${escapeHtml(pack.durationHours)} hours + rehearsal</dd></div>
        <div><dt>Main effort</dt><dd>${escapeHtml(Math.round(pack.eventNutrition.carbohydrateG))} g carbohydrate</dd></div>
        <div><dt>Contents</dt><dd>${escapeHtml(contents)}</dd></div>
      </dl>
      <div class="rsg-fuel-pack-card__foot"><strong>${money(price)}</strong><a href="${escapeHtml(detailUrl(pack, routeContext))}" data-rsg-pack-select="${escapeHtml(pack.id)}">View pack</a></div>
    </article>`;
  }

  function finderCallout(pack, source, routeContext = {}) {
    return `<aside class="rsg-fuel-packs__finder">
      <div><p class="rsg-v9__eyebrow">Need a different plan?</p><h2>Not your event or want to customise?</h2><p>Change the finish time, conditions, gut tolerance, caffeine, formats or favourite brands in Fuel Finder.</p></div>
      <a class="rsg-fuel-packs__button rsg-fuel-packs__button--orange" href="${escapeHtml(finderUrl(pack, source, routeContext))}" data-rsg-finder-handoff data-event-id="${escapeHtml(pack.eventId)}" data-brand="${escapeHtml(pack.brand)}" data-pack-id="${escapeHtml(pack.id)}">Customise in Fuel Finder</a>
    </aside>`;
  }

  function unavailableCards(registry, eventId, routeContext = {}) {
    const items = registry.unavailableChoices.filter((item) => item.eventId === eventId);
    if (!items.length) return '';
    const representative = registry.packs.find((pack) => pack.eventId === eventId);
    return `<div class="rsg-fuel-packs__unavailable"><h3>More brand options</h3><div class="rsg-fuel-packs__unavailable-grid">${items.map((item) => `<article><p class="rsg-fuel-pack-card__brand">${escapeHtml(item.brand)}</p><strong>Not available right now</strong><p>This brand pack is not available right now. Use Fuel Finder to build a plan from the products available now.</p>${representative ? `<a href="${escapeHtml(finderUrl({ ...representative, brand: '' }, 'fuel_pack', routeContext))}" data-rsg-finder-handoff data-event-id="${escapeHtml(eventId)}" data-brand="">Build a plan with Fuel Finder</a>` : ''}</article>`).join('')}</div></div>`;
  }

  function eventNavigation(registry, selected, routeContext = {}) {
    return `<nav class="rsg-fuel-packs__event-nav" aria-label="Choose a duration">${Object.entries(registry.events).map(([id, event]) => `<a href="${escapeHtml(eventUrl(id, routeContext))}"${id === selected ? ' aria-current="page"' : ''} data-rsg-distance-select="${escapeHtml(id)}"><span>${escapeHtml(event.label)}</span></a>`).join('')}</nav>`;
  }

  function landingEvent(root, registry, eventId) {
    const routeContext = contextFor(root);
    const event = registry.events[eventId] || registry.events['half-marathon'];
    const packs = registry.packs.filter((pack) => pack.eventId === eventId);
    const representative = packs[0];
    root.querySelector('[data-rsg-fuel-packs-title]').textContent = registry.labels.landingH1;
    root.querySelector('[data-rsg-fuel-packs-intro]').hidden = false;
    root.querySelector('[data-rsg-fuel-packs-view]').innerHTML = `${eventNavigation(registry, eventId, routeContext)}
      <section class="rsg-fuel-packs__event" aria-labelledby="RsgFuelPacksEvent-${safeId(eventId)}">
        <header class="rsg-fuel-packs__event-header"><div><p class="rsg-v9__eyebrow">${escapeHtml(routeContext.label)} starting point</p><h2 id="RsgFuelPacksEvent-${safeId(eventId)}">${escapeHtml(event.label)}</h2></div><p>${escapeHtml(event.useAssumption)}</p></header>
        <div class="rsg-fuel-packs__cards">${packs.map((pack) => packCard(pack, false, routeContext)).join('')}</div>
        ${unavailableCards(registry, eventId, routeContext)}
      </section>${representative ? finderCallout(representative, 'fuel_pack', routeContext) : ''}`;
    root.querySelector('[data-rsg-fuel-packs-view]').setAttribute('aria-busy', 'false');
    track('rsg_fuel_pack_distance_view', { source: 'fuel_packs_landing', eventId });
  }

  function packDetail(root, registry, pack) {
    const routeContext = contextFor(root);
    const event = registry.events[pack.eventId];
    const price = pack.livePricePence ?? pack.pricePence;
    const contents = pack.components.map((component) => `<li><strong>${component.quantity} × ${escapeHtml(component.saleUnitLabel)}</strong><span>${escapeHtml(component.productTitle)} · ${escapeHtml(component.variantTitle)}</span><small>${escapeHtml(component.eventServings)} main-effort serving${component.eventServings === 1 ? '' : 's'}${component.rehearsalServings ? ` + ${escapeHtml(component.rehearsalServings)} rehearsal serving${component.rehearsalServings === 1 ? '' : 's'}` : ''}</small></li>`).join('');
    root.querySelector('[data-rsg-fuel-packs-title]').textContent = pack.title;
    root.querySelector('[data-rsg-fuel-packs-intro]').hidden = true;
    const availabilityCopy = pack.liveVerified
      ? pack.liveAvailable ? 'Ready to add. Availability is checked again when the pack goes into your basket.' : 'This pack is temporarily unavailable.'
      : 'We could not confirm availability, so this pack cannot be added right now.';
    root.querySelector('[data-rsg-fuel-packs-view]').innerHTML = `<article class="rsg-fuel-pack-detail">
      <nav class="rsg-fuel-pack-detail__back" aria-label="Fuel Pack routes"><a href="${escapeHtml(eventUrl(pack.eventId, routeContext))}">All ${escapeHtml(event.label)} packs</a><a href="${escapeHtml(routeWithParams(routeContext.guideRoute || event.guideRoute))}">Read the guide</a></nav>
      <div class="rsg-fuel-pack-detail__summary">
        ${packPicture(pack, 'detail', routeContext)}
        <div><p class="rsg-v9__eyebrow">${escapeHtml(pack.brand)} · ${escapeHtml(event.label)}</p><p class="rsg-fuel-pack-detail__lead">${escapeHtml(pack.differentiator)}</p><p><strong>Assumption:</strong> ${escapeHtml(pack.useAssumption)}</p>${statusLabel(pack)}</div>
        <aside class="rsg-fuel-pack-detail__buy"><span>Pack price</span><strong>${money(price)}</strong><small>Includes everything listed below for the main effort and one rehearsal.</small><button class="rsg-fuel-packs__button" type="button" data-rsg-pack-add="${escapeHtml(pack.id)}"${pack.liveAvailable ? '' : ' disabled'}>Add complete pack</button><p data-rsg-pack-add-status role="status">${escapeHtml(availabilityCopy)}</p></aside>
      </div>
      ${supportingGallery(pack)}
      <div class="rsg-fuel-pack-detail__grid">
        <section><h2>Exactly what is included</h2><ul class="rsg-fuel-pack-detail__contents">${contents}</ul><p>${escapeHtml(pack.options)}</p></section>
        <section><h2>Fuel for the main effort</h2><ul class="rsg-fuel-pack-detail__nutrition">${nutritionSummary(pack, 'event')}</ul><p>This ${escapeHtml(event.durationHours)}-hour starting point sits within a ${escapeHtml(event.eventCarbRangeG[0])}–${escapeHtml(event.eventCarbRangeG[1])} g planning range. The pack also includes the rehearsal servings listed above.</p></section>
        <section><h2>How to use this starting point</h2><p>${escapeHtml(pack.timingGuide)}</p></section>
        <section><h2>What it does not cover</h2><p>${escapeHtml(pack.limitations)}</p></section>
      </div>
      ${finderCallout(pack, 'fuel_pack', routeContext)}
    </article>`;
    root.querySelector('[data-rsg-fuel-packs-view]').setAttribute('aria-busy', 'false');
    track('rsg_fuel_pack_view', { source: 'fuel_pack_detail', eventId: pack.eventId, brand: pack.brand, packId: pack.id, componentCount: pack.components.length, valuePence: price });
  }

  function rail(root, registry) {
    const routeContext = contextFor(root);
    const requested = (root.dataset.rsgFuelPacksEvents || '').split(',').map((value) => value.trim()).filter(Boolean);
    const eventIds = requested.filter((id) => registry.events[id]);
    const sections = eventIds.map((eventId) => {
      const event = registry.events[eventId];
      const packs = registry.packs.filter((pack) => pack.eventId === eventId);
      const representative = packs[0];
      return `<section class="rsg-fuel-packs__rail-event" aria-labelledby="RsgFuelPacksRail-${safeId(root.id)}-${safeId(eventId)}"><header><div><p class="rsg-v9__eyebrow">${escapeHtml(routeContext.label)} starting point</p><h3 id="RsgFuelPacksRail-${safeId(root.id)}-${safeId(eventId)}">${escapeHtml(event.label)}</h3></div><a href="${escapeHtml(eventUrl(eventId, routeContext))}">Compare all</a></header><div class="rsg-fuel-packs__rail-grid">${packs.map((pack) => packCard(pack, true, routeContext)).join('')}</div>${representative ? `<p class="rsg-fuel-packs__rail-custom"><a href="${escapeHtml(finderUrl(representative, 'guide_fuel_packs', routeContext))}" data-rsg-finder-handoff data-event-id="${escapeHtml(eventId)}">Different finish time, conditions or preferences? Customise in <span class="rsg-fuel-packs__finder-words">Fuel Finder</span>.</a></p>` : ''}</section>`;
    }).join('');
    root.querySelector('[data-rsg-fuel-packs-view]').innerHTML = sections || '<p>No ready-made fuel packs are available for this guide yet. Use Fuel Finder for a personalised plan.</p>';
    root.querySelector('[data-rsg-fuel-packs-view]').setAttribute('aria-busy', 'false');
  }

  async function rollbackInstance(instance) {
    let cartResponse;
    try {
      cartResponse = await fetch(`${ROOT_PATH}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    } catch (error) {
      return false;
    }
    if (!cartResponse.ok) return false;
    let cart;
    try {
      cart = await cartResponse.json();
    } catch (error) {
      return false;
    }
    if (!Array.isArray(cart?.items)) return false;
    const lines = cart.items.filter((item) => item.properties?._rsg_fuel_pack_instance === instance);
    if (!lines.length) return true;
    for (const line of lines) {
      try {
        await fetch(`${ROOT_PATH}cart/change.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: line.key, quantity: 0 }) });
      } catch (error) {
        // A final cart readback below is the source of truth, even when a removal request loses its response.
      }
    }
    let finalResponse;
    try {
      finalResponse = await fetch(`${ROOT_PATH}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    } catch (error) {
      return false;
    }
    if (!finalResponse.ok) return false;
    let finalCart;
    try {
      finalCart = await finalResponse.json();
    } catch (error) {
      return false;
    }
    if (!Array.isArray(finalCart?.items)) return false;
    return !finalCart.items.some((item) => item.properties?._rsg_fuel_pack_instance === instance);
  }

  async function verifyInstance(pack, instance) {
    const response = await fetch(`${ROOT_PATH}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) return false;
    const cart = await response.json();
    const actual = new Map();
    cart.items.filter((item) => item.properties?._rsg_fuel_pack_instance === instance).forEach((item) => actual.set(String(item.variant_id), (actual.get(String(item.variant_id)) || 0) + item.quantity));
    return pack.components.every((component) => actual.get(String(component.variantId)) === component.quantity) && actual.size === pack.components.length;
  }

  async function addPack(root, pack, button) {
    const status = root.querySelector('[data-rsg-pack-add-status]');
    const instance = `${pack.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = 'Adding the full pack…';
    const items = pack.components.map((component) => ({
      id: Number(component.variantId),
      quantity: component.quantity,
      properties: {
        'Fuel pack': pack.title,
        'Plan': `${pack.eventLabel} + rehearsal`,
        _rsg_fuel_pack_id: pack.id,
        _rsg_fuel_pack_instance: instance
      }
    }));
    try {
      const response = await fetch(`${ROOT_PATH}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      if (!response.ok) throw new Error('The complete pack could not be added.');
      if (!await verifyInstance(pack, instance)) throw new Error('The complete pack could not be confirmed in your basket.');
      track('rsg_fuel_pack_add', { source: 'fuel_pack_detail', eventId: pack.eventId, brand: pack.brand, packId: pack.id, componentCount: pack.components.length, valuePence: pack.livePricePence });
      if (status) status.textContent = 'Complete pack added. Opening your basket…';
      window.location.assign(`${ROOT_PATH}cart`);
    } catch (error) {
      let rollbackConfirmed = false;
      try { rollbackConfirmed = await rollbackInstance(instance); } catch (rollbackError) { /* fail closed in the message below */ }
      if (status) status.textContent = rollbackConfirmed
        ? 'The full pack could not be added, so nothing from it has been left in your basket. Choose another pack or try again later.'
        : 'We could not confirm that every pack item was removed. Please check your basket before trying again.';
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  function attachEvents(root, registry) {
    root.addEventListener('click', (event) => {
      const distance = event.target.closest('[data-rsg-distance-select]');
      if (distance) track('rsg_fuel_pack_distance_select', { source: root.dataset.rsgFuelPacksScope, eventId: distance.dataset.rsgDistanceSelect });
      const packLink = event.target.closest('[data-rsg-pack-select]');
      if (packLink) {
        const pack = registry.packs.find((item) => item.id === packLink.dataset.rsgPackSelect);
        if (pack) track('rsg_fuel_pack_brand_select', { source: root.dataset.rsgFuelPacksScope, eventId: pack.eventId, brand: pack.brand, packId: pack.id, valuePence: pack.livePricePence });
      }
      const finder = event.target.closest('[data-rsg-finder-handoff]');
      if (finder) track('rsg_fuel_pack_finder_handoff', { source: root.dataset.rsgFuelPacksScope, eventId: finder.dataset.eventId, brand: finder.dataset.brand, packId: finder.dataset.packId });
      const add = event.target.closest('[data-rsg-pack-add]');
      if (add) {
        const pack = registry.packs.find((item) => item.id === add.dataset.rsgPackAdd);
        if (pack && pack.liveAvailable) addPack(root, pack, add);
      }
    });
  }

  async function initialise(root) {
    if (root.dataset.rsgFuelPacksReady === 'true') return;
    if (root.dataset.rsgFuelPacksScope === 'landing' && !isFuelPacksRoute()) return;
    root.dataset.rsgFuelPacksReady = 'true';
    const registry = registryFor(root);
    if (!registry || registry.targetThemeId !== THEME_ID || root.dataset.rsgFuelPacksTargetTheme !== THEME_ID) {
      root.querySelector('[data-rsg-fuel-packs-view]').innerHTML = '<p class="rsg-fuel-packs__notice">Fuel Packs are temporarily unavailable. Please try again shortly.</p>';
      return;
    }
    const inventory = inventoryFor(root);
    attachEvents(root, registry);
    await refreshRegistry(registry, inventory);
    if (root.dataset.rsgFuelPacksScope === 'rail') return rail(root, registry);
    const params = new URLSearchParams(window.location.search);
    const pack = registry.packs.find((item) => item.id === params.get('pack'));
    if (pack) packDetail(root, registry, pack);
    else landingEvent(root, registry, registry.events[params.get('distance')] ? params.get('distance') : root.dataset.rsgFuelPacksDefaultEvent);
  }

  const initialiseAll = (scope = document) => scope.querySelectorAll('[data-rsg-fuel-packs-root]').forEach(initialise);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true });
  else initialiseAll();
  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
